import { Bookmark, BookmarkBorder } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { InView } from 'react-intersection-observer';

import {
  CardFlair,
  CardFlairVariants,
  CreditsPill,
  DurationPill,
  EthicsPill,
} from '@/components';
import { ComplianceTypes } from '@/constants';
import { EVENT_CONSTANTS } from '@/services';

import { Link, LinkVariants } from '../../../Link';
import {
  EpisodeCardSmallDimensions,
  EpisodeCardVariants,
} from '../../constants';
import { episodeCardStyles as styles } from '../../styles';
import { EpisodeCardComponentProps } from '../../useEpisodeCard';
import { EpisodeToolbar } from '../EpisodeToolbar';
import { EpisodeCardSmallSkeleton } from './EpisodeCardSmallSkeleton';

interface EpisodeCardSmallProps extends EpisodeCardComponentProps {
  onLinkClick?: () => void;
}

export const EpisodeCardSmall = ({
  episode,
  onLinkClick,
  referrer = {},
  navigationLink,
  impressionRef,
  onToggleBookmark,
  isBookmarked,
  coverLoaded,
  setCoverLoaded,
  hideToolbar,
  isEpisodeEthics,
  compliance,
}: EpisodeCardSmallProps) => {
  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.EPISODE_CARD,
    variants: {
      ...referrer.variants,
      episodeCardVariant: EpisodeCardVariants.SMALL,
    },
  };

  const isCPE = compliance === ComplianceTypes.CPE;

  return (
    <Box
      ref={impressionRef}
      sx={[styles.root, { width: EpisodeCardSmallDimensions.width, gap: 1 }]}>
      <Link
        to={navigationLink}
        onClick={onLinkClick}
        state={{ referrer: location }}>
        <InView rootMargin="1000px 0px 1000px 0px" threshold={0} triggerOnce>
          {({ inView, ref: coverRef }) => (
            <Box
              sx={[
                styles.cover,
                {
                  height: EpisodeCardSmallDimensions.coverImageWidth,
                  width: EpisodeCardSmallDimensions.coverImageWidth,
                },
              ]}
              ref={coverRef}
              id="episode-card-cover">
              {inView && (
                <>
                  <Box
                    component="img"
                    id="episode-card-img"
                    sx={[
                      styles.coverImg,
                      styles.coverImgTall,
                      { display: coverLoaded ? 'block' : 'none' },
                    ]}
                    src={episode.cover}
                    alt="cover"
                    onLoad={() => setCoverLoaded(true)}
                  />
                  {episode.episodeAgeTag === 'new' && (
                    <CardFlair variant={CardFlairVariants.SMALL} />
                  )}
                  <Box
                    component="img"
                    sx={[
                      styles.companyLogoImg,
                      {
                        height: 32,
                        width: 32,
                      },
                      { display: coverLoaded ? 'block' : 'none' },
                    ]}
                    src={episode.companyLogo}
                    alt="company logo"
                  />
                </>
              )}
              {!coverLoaded && <EpisodeCardSmallSkeleton />}
            </Box>
          )}
        </InView>
      </Link>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flex: 1,
        }}>
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={[styles.tagContainer, { my: 1 }]}>
              {isEpisodeEthics && !isCPE && <EthicsPill />}
              {isCPE ? (
                <CreditsPill credits={episode.credits} />
              ) : (
                <DurationPill duration={episode.duration} />
              )}
            </Box>
            {isBookmarked ? (
              <IconButton
                onClick={onToggleBookmark}
                disableRipple
                aria-label="Remove bookmark"
                sx={styles.bookmarkIconButton}>
                <Bookmark
                  sx={[styles.bookmarkIcon, styles.bookmarkIconActive]}
                />
              </IconButton>
            ) : (
              <IconButton
                onClick={onToggleBookmark}
                disableRipple
                aria-label="Add bookmark"
                sx={styles.bookmarkIconButton}>
                <BookmarkBorder sx={styles.bookmarkIcon} />
              </IconButton>
            )}
          </Box>
          <Link
            variant={LinkVariants.PLAIN}
            to={navigationLink}
            onClick={onLinkClick}
            state={{ referrer: location }}>
            <Box
              id="episode-card-text"
              sx={[styles.titleContainer, { WebkitLineClamp: 1 }]}>
              <Typography variant="body3SemiBold">{episode.name}</Typography>
            </Box>
            {isCPE && episode.fieldOfStudy && (
              <Box id="episode-card-field-of-study" sx={{ WebkitLineClamp: 1 }}>
                <Typography variant="caption1Bold" sx={{ color: 'text.light' }}>
                  {episode.fieldOfStudy}
                </Typography>
              </Box>
            )}
          </Link>
        </Box>
        <Box id="episode-card-toolbar" sx={[hideToolbar && styles.hideToolbar]}>
          <EpisodeToolbar episode={episode} referrer={location} />
        </Box>
      </Box>
    </Box>
  );
};
